import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { TooltipProps } from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import { IgTypography } from "@intelligems/ig-theme";
import { Box, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { selectedVariations } from "@src/store/slices/variations";
import { StackedBarMetricData } from "@src/types/analytics";

const fontSize = "14px";
const lineHeight = "16px";
const letterSpacing = ".4px";

const getFocusedCellData = (
  singleGroupMetricData: StackedBarMetricData,
  hoveredCell: string
) => {
  if (hoveredCell.includes("Unit")) {
    const formattedHoveredCellNumber = hoveredCell.replace(
      /^(\d+)\+?\s+Units?$/i,
      "$1"
    );
    try {
      const parsedHoveredCellNumber = parseInt(formattedHoveredCellNumber);
      return singleGroupMetricData.stackedHorizValues![parsedHoveredCellNumber];
    } catch {
      return null;
    }
  } else {
    // product group graph so exact string match
    return singleGroupMetricData.stackedHorizValues![hoveredCell];
  }
};

export const IgHorizStackedBarTooltip = (
  props: TooltipProps<ValueType, NameType> & {
    groupMetricData: StackedBarMetricData[];
    hoveredCell: string;
  }
) => {
  const theme = useTheme();
  const variations = useSelector(selectedVariations);
  const { active, payload, groupMetricData, hoveredCell } = props;

  const focusedCellData = useMemo(() => {
    return groupMetricData.map((group) => {
      return {
        [variations.find((variation) => variation.id === group.variation_id)
          ?.name || "Test Group"]: getFocusedCellData(group, hoveredCell),
      };
    });
  }, [hoveredCell]);
  if (!active || !payload) return null;

  if (!focusedCellData) return null;

  return (
    <Box
      sx={{
        padding: "10px",
        borderRadius: "5px",
        minWidth: "150px",
        backgroundColor: "rgba(73, 76, 80, 0.92)",
      }}
    >
      <Grid container direction={"column"}>
        <Grid>
          <IgTypography
            sx={{
              color: theme.igPrimitives.gray[500],
              fontSize,
              fontWeight: 700,
              letterSpacing,
              lineHeight,
            }}
          >
            {hoveredCell}
          </IgTypography>
        </Grid>
        {focusedCellData.map((item, index) => {
          const itemKey = Object.keys(item)[0];
          const itemVal = item[itemKey];
          return (
            <Grid
              key={`${itemKey}-${index}`}
              container
              direction={"row"}
              justifyContent={"space-between"}
            >
              <Grid>
                <IgTypography
                  sx={{
                    color: theme.igPrimitives.gray[500],
                    fontSize,

                    letterSpacing,
                    lineHeight,
                  }}
                >
                  {itemKey}
                </IgTypography>
              </Grid>
              <Grid>
                <IgTypography
                  sx={{
                    color: theme.igPrimitives.gray[500],
                    fontSize,
                    letterSpacing,
                    lineHeight,
                  }}
                >
                  {Math.round(itemVal || 0)}%
                </IgTypography>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
